<svelte:options customElement={{ tag: "bt-curation-publish-preview"}} />

<script lang="ts">
  import { modalStore } from '$shared/modal/modalStore';
  import Modal from '$shared/modal/Modal.svelte';
  import Icon from '$shared/icons/Icon.svelte';
  import { eventBus } from '$shared/lib/eventBus';

  export let showEmailToggle: string = 'false';
  $: showEmailToggleBool = showEmailToggle === 'true';
  export let showSetRemindersButton: string = 'false';
  $: showSetRemindersButtonBool = showSetRemindersButton === 'true';
  export let publishUrl: string = '';
  export let copyUrl: string = '';
  export let orgname: string = '';

  // Email toggle translations. Description is split so "User Management" links to /users.
  export let sendEmailToggleLabel: string = 'Send email on Publish';
  export let sendEmailToggleDescriptionPrefix: string = 'Everyone in';
  export let userManagementLinkText: string = 'User Management';
  export let userManagementUrl: string = '/users';
  export let sendEmailToggleDescriptionSuffix: string = 'will be invited to start this curation.';
  export let sendEmailToggleLinkPrefix: string = 'Preview the email on our';
  export let sendEmailToggleLinkText: string = 'help center';

  // Modal translations
  export let publishCurationTitle: string = 'Publish curation';
  export let cancelText: string = 'Cancel';
  export let publishedModalTitle: string = 'Your curation is published';
  export let publishedToLabel: string = 'Published to:';
  export let everyoneAtOrg: string = 'Everyone at';
  export let publishedEmailInvitedText: string = '';
  export let remindersCalloutText: string = 'Set email reminders to engage your learners, or copy the link to send it directly.';
  export let setRemindersText: string = 'Set reminders';
  $: invitedText = publishedEmailInvitedText || `Everyone at ${orgname} has been invited to start this curation.`;
  export let copyLinkText: string = 'Copy link to curation';
  export let copiedText: string = 'Copied';
  export let returnToCurationText: string = 'Return to curation';
  export let invalidEmailsLabel: string = 'invalid emails';
  export let invalidEmailLabel: string = 'invalid email';
  export let invalidEmailPrefix: string = 'Your curation is successfully published, but';
  export let invalidEmailSuffix: string = "learners with invalid email addresses weren't notified. Visit our";
  export let invalidEmailHelpText: string = 'help center';
  export let invalidEmailPostfix: string = 'to learn how you can update or add valid email addresses.';

  // Warning text translations
  export let warningTextIntro: string = 'Before publishing, we recommend you check the following:';
  export let warningTextItem1: string = 'Your curation has a title and description.';
  export let warningTextItem2: string = 'You have removed empty sections.';
  export let warningTextItem3: string = 'All sections have titles and descriptions.';
  export let publishingText: string = 'Publishing…';
  export let publishText: string = 'Publish';

  let slotContainer: Element;

  $: modalName = $modalStore.modalName

  let curationDropdown = document.getElementById('curation-dropdown');

  let sendEmailOnPublish = false;
  let isPublishing = false;
  let linkCopied = false;
  let linkCopyTimeout: number | undefined;
  let invalidEmailCount = 0;

  $: {
    if (modalName === 'previewCuration') {
      document.querySelectorAll('.publish-preview-slot').forEach(slotEl => {
        slotEl.removeAttribute('hidden')
      });
      curationDropdown.classList.add('hidden');
    }
  }

  async function handlePublish() {
    if (isPublishing || !publishUrl) return;
    isPublishing = true;

    try {
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.getAttribute('content') ?? '';
      const resp = await fetch(publishUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ send_email_on_publish: sendEmailOnPublish }),
      });

      const data = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        console.error('Publish failed', data);
        return;
      }

      // Update share modal published state
      const shareModal = document.querySelector<HTMLElement>('bt-curation-share-modal');
      if (shareModal) shareModal.setAttribute('publishedState', 'true');

      // Update the published badge in the header
      const badgesEl = document.getElementById('curation-assignment-badges');
      if (badgesEl) {
        badgesEl.innerHTML = '<bt-badge design="positive" text="Published"></bt-badge>';
      }

      // Reveal the reminders button now that the curation is published
      const remindersBtn = document.getElementById('curation-reminders-button');
      if (remindersBtn) remindersBtn.removeAttribute('hidden');

      // Keep the reminders panel current without a reload
      eventBus.emit('curation-published');
      if (sendEmailOnPublish) {
        eventBus.emit('curation-published-email-sent');
      }

      // Show published confirmation or error modal
      invalidEmailCount = data.invalid_email_count ?? 0;
      if (sendEmailOnPublish && invalidEmailCount > 0) {
        modalStore.setName('publishedCurationError');
      } else {
        modalStore.setName('publishedCuration');
      }
    } catch (e) {
      console.error('Publish failed', e);
    } finally {
      isPublishing = false;
    }
  }

  async function copyLink() {
    if (!copyUrl) return;
    try {
      await navigator.clipboard.writeText(copyUrl);
      linkCopied = true;
      if (linkCopyTimeout) window.clearTimeout(linkCopyTimeout);
      linkCopyTimeout = window.setTimeout(() => { linkCopied = false; }, 2000);
    } catch (e) {
      console.error('Failed to copy link', e);
    }
  }

  function closeModal() {
    modalStore.setName('');
  }

  function openReminders() {
    modalStore.setName('curationReminders');
  }
</script>

{#if modalName === 'previewCuration'}
  <Modal modalTitle={publishCurationTitle} showCloseIcon={true} showFooter={false} width="wide">
    <div class="card-and-warning" slot="modal-contents">
      <div class="preview-card" bind:this={slotContainer}>
        <slot name="contents"></slot>
      </div>
      <div class="right-column">
        <div class="right-column-top">
          <div class="warning-text">
            {warningTextIntro}
            <ul>
              <li>{warningTextItem1}</li>
              <li>{warningTextItem2}</li>
              <li>{warningTextItem3}</li>
            </ul>
          </div>
          {#if showEmailToggleBool}
          <div class="email-toggle-container">
            <label class="toggle-row">
              <input
                type="checkbox"
                class="toggle-input"
                bind:checked={sendEmailOnPublish}
              />
              <span class="toggle-label">{sendEmailToggleLabel}</span>
            </label>
            <p class="toggle-description">
              {sendEmailToggleDescriptionPrefix} <a href={userManagementUrl} target="_blank" class="toggle-link">{userManagementLinkText}</a> {sendEmailToggleDescriptionSuffix}
              {sendEmailToggleLinkPrefix} <a href="https://help.bigthink.com/help/share-a-curation" target="_blank" class="toggle-link">{sendEmailToggleLinkText}</a>.
            </p>
          </div>
          {/if}
        </div>
        <div class="preview-ctas">
          <button
            type="button"
            class="cancel-btn"
            on:click={closeModal}
          >
            {cancelText}
          </button>
          <button
            type="button"
            class="publish-btn"
            on:click={handlePublish}
            disabled={isPublishing}
          >
            {isPublishing ? publishingText : publishText}
          </button>
        </div>
      </div>
    </div>
  </Modal>
{/if}

{#if modalName === 'publishedCuration'}
  <Modal modalTitle={publishedModalTitle} showCloseIcon={true} showFooter={false} width="medium">
    <div slot="modal-contents" class="published-body">
      <p class="published-row"><span class="published-row__label">{publishedToLabel}</span> {everyoneAtOrg} </p>
      {#if sendEmailOnPublish}
        <p class="published-row">{invitedText}</p>
      {/if}
      {#if showSetRemindersButtonBool}
        <div class="reminders-callout">
          <span class="reminders-callout__icon"><Icon icon="Info" size="medium" /></span>
          <span>{remindersCalloutText}</span>
        </div>
      {/if}
      <div class="published-ctas">
        <button type="button" class="cta-outline" on:click={copyLink}>
          <Icon icon="Link" size="small" />
          {#if linkCopied}<Icon icon="Check" size="small" />{copiedText}{:else}{copyLinkText}{/if}
        </button>
        {#if showSetRemindersButtonBool}
          <button type="button" class="cta-filled" on:click={openReminders}>
            {setRemindersText}
          </button>
        {:else}
          <button type="button" class="cta-filled" on:click={closeModal}>
            {returnToCurationText}
          </button>
        {/if}
      </div>
    </div>
  </Modal>
{/if}

{#if modalName === 'publishedCurationError'}
  <Modal modalTitle={publishedModalTitle} showCloseIcon={true} showFooter={false} width="medium">
    <div slot="modal-contents" class="published-body">
      <div class="email-error-alert">
        <div class="email-error-alert__header">
          <span style="display: flex; color: #EF4444"><Icon icon="CircleX" size="medium" /></span>
          <span>{invalidEmailCount} {invalidEmailCount === 1 ? invalidEmailLabel : invalidEmailsLabel}</span>
        </div>
      </div>
      <p class="published-row">{invalidEmailPrefix} {invalidEmailSuffix} <a href="https://help.bigthink.com/help/share-a-curation" target="_blank" class="toggle-link">{invalidEmailHelpText}</a> {invalidEmailPostfix}</p>
      <div class="published-ctas">
        <button type="button" class="cta-outline" on:click={copyLink}>
          <Icon icon="Link" size="small" />
          {#if linkCopied}<Icon icon="Check" size="small" />{copiedText}{:else}{copyLinkText}{/if}
        </button>
        <button type="button" class="cta-filled" on:click={closeModal}>
          {returnToCurationText}
        </button>
      </div>
    </div>
  </Modal>
{/if}

<style>
  .modal {
    position: fixed;
    width: 100%;
    height: 100%;
    z-index: 50;
    top: 0;
    left: 0;
    padding: theme("padding.2");
  }
  .card-and-warning {
    display: flex;
    flex-direction: row;
    gap: 24px;
    @media (max-width: theme('screens.sm')) {
      flex-direction: column;
      max-height: calc(100vh - 8rem);
      overflow-y: auto;
    }
  }

  .preview-card {
    width: 327px;
    height: 446px;
    flex-shrink: 0;
    overflow: hidden;
    @media (max-width: theme('screens.sm')) {
      width: 100%;
    }
  }

  .right-column {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex: 1;
  }

  .right-column-top {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .warning-text {
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
  }

  .email-toggle-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .preview-ctas {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 24px;
  }

  .cancel-btn {
    background: none;
    border: none;
    padding: 2px;
    font-size: 14px;
    font-weight: 500;
    font-family: theme('fontFamily.sans');
    line-height: 20px;
    color: #000000;
    cursor: pointer;
    &:hover { text-decoration: underline; }
  }

  .toggle-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    cursor: pointer;
  }

  .toggle-label {
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    color: #000000;
  }

  .toggle-description {
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    color: #737373;
    margin: 0;
  }

  .toggle-link {
    text-decoration: underline;
    color: #737373;
  }

  .toggle-input {
    appearance: none;
    -webkit-appearance: none;
    position: relative;
    flex-shrink: 0;
    width: 26px;
    height: 16px;
    margin: 0;
    border-radius: 51px;
    background: #D4D4D4;
    border: none;
    cursor: pointer;
    transition: background 0.2s;
    &::before {
      content: '';
      position: absolute;
      top: 1px;
      left: 1px;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: #FFFFFF;
      box-shadow: 0px 1.5px 0.5px 0px rgba(0,0,0,0.06), 0px 1.5px 4px 0px rgba(0,0,0,0.15), 0px 0px 0px 0.5px rgba(0,0,0,0.04);
      transition: transform 0.2s;
    }
    &:checked {
      background: #1C9FFE;
      &::before {
        transform: translateX(10px);
      }
    }
  }

  .publish-btn {
    padding: 10px 24px;
    background: theme('colors.neutral.900');
    color: theme('colors.white');
    border: none;
    border-radius: theme('borderRadius.lg');
    font-weight: 500;
    font-family: theme('fontFamily.sans');
    font-size: theme('fontSize.sm');
    cursor: pointer;
    &:hover { background: theme('colors.neutral.800'); }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
  }

  .reminders-callout {
    display: flex;
    align-items: center;
    gap: theme('spacing.4');
    align-self: stretch;
    background: theme('colors.sky.50');
    border: 1px solid theme('colors.sky.100');
    border-radius: theme('borderRadius.lg');
    padding: theme('padding.4');
    font-size: theme('fontSize.sm');
    font-weight: 400;
    line-height: 21px;
    color: theme('colors.neutral.500');
  }

  .reminders-callout__icon {
    display: flex;
    color: theme('colors.sky.500');
  }

  /* Error alert */
  .email-error-alert {
    background: #FEF2F2;
    border: 1px solid #FEE2E2;
    border-radius: 8px;
    padding: 16px;
  }

  .email-error-alert__header {
    display: flex;
    align-items: center;
    gap: 16px;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #737373;
  }

  /* Published modal */
  .published-body {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .published-row {
    margin: 0;
    color: theme('colors.black');
    font-family: theme('fontFamily.sans');
    font-size: theme('fontSize.base');
    font-weight: 400;
    line-height: theme('lineHeight.6');
    @media (max-width: theme('screens.sm')) {
      font-size: theme('fontSize.sm');
      line-height: 21px;
    }
  }

  .published-row__label {
    font-weight: 500;
  }

  .published-ctas {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: theme('spacing.2');
    @media (max-width: theme('screens.sm')) {
      flex-direction: column;
      align-items: flex-end;
      gap: theme('spacing.4');
    }
  }

  .cta-outline {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: theme('spacing.10');
    padding: 0 theme('padding.4');
    background: theme('colors.white');
    border: 1px solid #D4D4D4;
    border-radius: theme('borderRadius.lg');
    font-weight: 500;
    font-size: theme('fontSize.sm');
    font-family: theme('fontFamily.sans');
    cursor: pointer;
    &:hover { background: theme('colors.neutral.100'); }
  }

  .cta-filled {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: theme('spacing.10');
    padding: 0 theme('padding.4');
    background: theme('colors.black');
    color: theme('colors.white');
    border: none;
    border-radius: theme('borderRadius.lg');
    font-weight: 500;
    font-size: theme('fontSize.sm');
    font-family: theme('fontFamily.sans');
    cursor: pointer;
    &:hover { background: theme('colors.neutral.800'); }
  }
</style>
